'use client';

/**
 * @fileoverview `CategoryFilterAnnouncer` — client component that
 * announces sub-genre filter changes on the category hub page (WEB-132).
 *
 * Reads `?genre=` (or legacy `?genres=`) from `useSearchParams()` and
 * pushes a screen-reader-friendly message into a polite ARIA live
 * region. Whenever the URL changes via `SubGenreChipsConnected.replace`,
 * Next re-renders this component and the new message is announced.
 *
 * Wording follows the WEB-132 AC:
 *   `Showing N events in <category>, filtered to <genre>`
 *
 * Unknown genre slugs (deep links from older builds, or values with no
 * matching sub-genre count) degrade gracefully to a "Filtered to"
 * variant without claiming a specific count we don't have.
 *
 * Pure presentational client wrapper around `AnnouncementRegion`. The
 * page owns the data — totals and sub-genre map come in as props.
 *
 * @since WEB-132
 */
import * as React from 'react';
import { useSearchParams } from 'next/navigation';
import { AnnouncementRegion } from '@/components/atoms/AnnouncementRegion';
import { categoryDisplayLabel } from '@/lib/city-home/categoryLabelCase';

export interface CategoryFilterAnnouncerSubGenre {
  slug: string;
  label: string;
  /** Filtered event count for this sub-genre, used in the message. */
  count: number;
}

export interface CategoryFilterAnnouncerProps {
  /** Display label for the category, e.g. "Music". */
  categoryLabel: string;
  /** Total event count for the unfiltered category. */
  totalCount: number;
  /** Sub-genres available on this category surface. */
  subGenres: ReadonlyArray<CategoryFilterAnnouncerSubGenre>;
}

const GENRE_PARAM = 'genre';
const LEGACY_GENRES_PARAM = 'genres';

/**
 * Resolve the active genre slug from URL state. Mirrors
 * `SubGenreChipsConnected.resolveSelectedSlug` so chip state and
 * announcer state stay in lockstep.
 */
function resolveActiveGenreSlug(searchParams: URLSearchParams): string | null {
  const canonical = searchParams.get(GENRE_PARAM);
  if (canonical !== null && canonical.length > 0) return canonical;
  const legacy = searchParams.get(LEGACY_GENRES_PARAM);
  if (legacy === null) return null;
  const firstSlug = legacy
    .split(',')
    .map((slug) => slug.trim())
    .find((slug) => slug.length > 0);
  return firstSlug ?? null;
}

function buildMessage({
  categoryLabel,
  totalCount,
  activeGenreSlug,
  subGenres,
}: {
  categoryLabel: string;
  totalCount: number;
  activeGenreSlug: string | null;
  subGenres: ReadonlyArray<CategoryFilterAnnouncerSubGenre>;
}): string {
  const label = categoryDisplayLabel(categoryLabel);
  if (activeGenreSlug === null) {
    return `Showing ${totalCount} events in ${label}`;
  }

  const match = subGenres.find((g) => g.slug === activeGenreSlug);
  if (match) {
    return `Showing ${match.count} events in ${label}, filtered to ${match.label}`;
  }

  // Unknown slug — keep the announcement informative without a
  // fabricated count.
  return `Filtered to ${activeGenreSlug} in ${label}`;
}

export function CategoryFilterAnnouncer({
  categoryLabel,
  totalCount,
  subGenres,
}: CategoryFilterAnnouncerProps): React.ReactElement {
  const searchParams = useSearchParams();
  const activeGenreSlug = resolveActiveGenreSlug(searchParams);
  const message = buildMessage({ categoryLabel, totalCount, activeGenreSlug, subGenres });

  return <AnnouncementRegion message={message} politeness="polite" />;
}
